import programs from '@app/programs'
import { getProductId, isPremiumOfferPrefix } from './constants'

export function getValueByPurchasedProduct(program: string, purchaseSerial: string, tradedManufacturer: string) {
  const purchaseSku = purchaseSerial?.slice(0, 6)

  /** Maps the `purchaseSku` to an offer rule name */
  const product = getProductId(purchaseSku)

  /** Return an offer of `0` if the sku cannot be mapped to a product */
  if (!product) {
    return 0
  }

  /** Find the offer rules for the products */
  const productOfferRule = programs[program!].offer_rules[product]?.find(rule => rule.with === tradedManufacturer)

  /** Flag whether a rule exists */
  const productHasOfferRule = Boolean(productOfferRule) && productOfferRule !== undefined

  /** Return `0` if no offer rule exists */
  if (!productHasOfferRule) {
    return 0
  }

  /** Return the matched offer based on program, purchase, and traded products  */
  return productOfferRule.value
}

/**
 * Determines the amount to be paid for the trade-in based on promotions offer rules
 * @param program The program code
 * @param serialNumber The serial number of the traded product
 * @param manufacturer The manufacturer of the traded product
 * @returns A number representing the value of the traded product
 */
export function getValueByTradedProduct(program: string, serialNumber: string, manufacturer: string) {
  const tradingDyson = manufacturer === 'Dyson'
  let productOfferRule = programs[program!].offer_rules.global.find(rule => rule.with === manufacturer)

  if (tradingDyson) {
    const isPremium = isPremiumOfferPrefix(serialNumber)
    const offerName = isPremium ? 'Dyson_Premium' : 'Dyson_Other'

    /** Find the offer rules for the products */
    productOfferRule = programs[program!].offer_rules.global.find(rule => rule.with === offerName)
  }

  if (!productOfferRule) {
    return 0
  }

  return productOfferRule.value
}

/**
 * Retrieves the Apollo SKU of the purchased product based on the product name.
 * @param productName - The display name of the purchased product
 * @returns a 3-letter SKU for the purchased product
 */
export function getPurchaseModelSku(productName: string) {
  switch (productName) {
    case 'Dyson Gen5detect™ Absolute':
      return 'GN5'
    case 'Dyson V15s Submarine™':
      return 'V15'
    case 'Dyson WashG1™':
      return 'WG1'
    default:
      return ''
  }
}

/**
 * Retrieves the traded SKU based on the serial number and manufacturer.
 * @param {string} serialNumber - The serial number of the product.
 * @param {string} manufacturer - The manufacturer of the product.
 * @param {string} purchaseModel - The display name of the purchased product.
 * @returns {string} The offer SKU.
 */
export function getTradedSku(serialNumber: string, manufacturer: string, purchaseModel: string) {
  const tradingDyson = manufacturer === 'Dyson'
  const pSkuPart = getPurchaseModelSku(purchaseModel)

  if (tradingDyson) {
    const isPremium = isPremiumOfferPrefix(serialNumber)
    return isPremium ? `WB-SA-PDYSON-FAC-${pSkuPart}` : `WB-SA-ODYSON-FAC-${pSkuPart}`
  }

  const isOtherPremium = manufacturer !== 'Other'
  return isOtherPremium ? `WB-SA-PNODYS-FAC-${pSkuPart}` : `WB-SA-NOTDYS-FAC-${pSkuPart}`
}

/**
 * Retrieves the Apollo traded SKU based on the serial number and manufacturer
 * for de and at programmes between 15/09/2025 - 15/10/2025
 * @param {string} manufacturer - The manufacturer of the traded product.
 * @param {string} purchaseSerialNumber - The serial number of the purchased product.
 * @returns {string} The offer SKU.
 */
export function getTradedSkuForDEAndAT(manufacturer: string, purchaseSerialNumber: string) {
  // serialNumber eg: 3BR-EU-FER8645A
  const shortSerial = purchaseSerialNumber.slice(0, 3)

  const tradingDyson = manufacturer === 'Dyson'
  // All non-Dyson products prefix with `FC` regardless of product category
  if (!tradingDyson) {
    return `FC-DY-NOTDYS-${shortSerial}`
  }

  switch (shortSerial) {
    // Dyson V15s Detect Submarine™
    case '3BR':
      return 'FC-DY-DV15SS-3BR'
    // Dyson Gen5detect™ Absolute
    case 'Z5S':
    case 'Z5T':
      return `FC-DY-DG5DEA-${shortSerial}`
    // Dyson V16 Piston Animal
    case '7RD':
      return 'FC-DY-DPISTA-7RD'
    // Dyson V16 Piston Animal Submarine™
    // Dyson V16 Piston Animal Submarine™ (Soft Bundle)
    case '7RE':
      // OR FC-DY-DPASSB-7RE?
      return 'FC-DY-DPISAS-7RE'
    // Dyson V16 Piston Animal + Detailed Cleaning Kit
    case '7RF':
      return 'FC-DY-DPADCK-7RF'
    // Dyson Airwrap Co-anda2x™
    case '5PC':
    case '6PS':
    case '5PW':
    case '7PA':
    case '6UT':
    case '7UJ':
      return `HC-DY-DAIRC2-${shortSerial}`
    // Dyson Airwrap i.d.™
    case '9SN':
    case '1TD':
    case '1TP':
    case '2TH':
    case '5UN':
    case '6UF':
      return `HC-DY-DAIRID-${shortSerial}`
    default:
      return ''
  }
}
